import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePreAuthFeatureFlags } from '../../hooks/usePreAuthFeatureFlags';
import { Shield, Mail, Lock, Building, User, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import '../../styles/vds-auth.css';

const SignupPage = () => {
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') || '';
  
  const [accountType, setAccountType] = useState(initialType);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();
  
  // A5: Use shared pre-auth feature flags hook
  const { isEmployerPlatformLive, loading: flagsLoading } = usePreAuthFeatureFlags();
  const employerPlatformLive = isEmployerPlatformLive();

  // A5: Redirect if employer type is selected but platform is not live
  useEffect(() => {
    if (!flagsLoading && initialType === 'employer' && !employerPlatformLive) {
      toast.info('Employer signup opens with the employer platform launch');
      navigate('/#employers');
    }
  }, [initialType, navigate, employerPlatformLive, flagsLoading]);

  // A5: Handle employer type selection when platform is not live
  const handleAccountTypeChange = (type) => {
    if (type === 'employer' && !employerPlatformLive) {
      toast.info('Employer signup opens with the employer platform launch');
      navigate('/#employers');
      return;
    }
    setAccountType(type);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!accountType) {
      toast.error('Please select an account type');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (!termsAccepted) {
      toast.error('Please accept the Terms of Service and Privacy Policy');
      return;
    }
    
    setLoading(true);
    
    try {
      const user = await signup(email, password, accountType, termsAccepted);
      toast.success('Account created successfully!');
      // Redirect employers to onboarding, employees to dashboard
      navigate(user.account_type === 'employer' ? '/employer/onboarding' : '/employee/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="vds-auth-page">
      <div className="vds-auth-container" style={{ maxWidth: '480px' }}>
        <div className="vds-auth-header">
          <Link to="/" className="vds-auth-logo">
            <div className="vds-auth-logo-mark">
              <Shield className="h-5 w-5" />
            </div>
            <span className="vds-auth-logo-text">VETT</span>
          </Link>
          <h1 className="vds-auth-title">Create your account</h1>
          <p className="vds-auth-subtitle">Join VETT to get started</p>
        </div>

        <div className="vds-auth-card">
          <form onSubmit={handleSubmit} className="vds-auth-form">
            {/* Account Type Selection */}
            <div className="vds-form-group">
              <label className="vds-form-label">I am...</label>
              <div className="vds-type-grid">
                <div
                  onClick={() => handleAccountTypeChange('employer')}
                  className={`vds-type-card ${accountType === 'employer' ? 'selected' : ''} ${!employerPlatformLive ? 'disabled' : ''}`}
                  data-testid="signup-employer-type"
                >
                  <div className="vds-type-icon">
                    <Building className="h-6 w-6" />
                  </div>
                  <div className="vds-type-title">An Employer</div>
                  <div className="vds-type-subtitle">Hire verified talent</div>
                  <span className="vds-type-badge">14-day free trial</span>
                  {accountType === 'employer' && (
                    <div className="vds-type-check">
                      <Check className="h-5 w-5" />
                    </div>
                  )}
                </div>
                
                <div
                  onClick={() => setAccountType('employee')}
                  className={`vds-type-card ${accountType === 'employee' ? 'selected' : ''}`}
                  data-testid="signup-employee-type"
                >
                  <div className="vds-type-icon">
                    <User className="h-6 w-6" />
                  </div>
                  <div className="vds-type-title">A Professional</div>
                  <div className="vds-type-subtitle">Build your record</div>
                  <span className="vds-type-badge">Always free</span>
                  {accountType === 'employee' && (
                    <div className="vds-type-check">
                      <Check className="h-5 w-5" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="vds-form-group">
              <label className="vds-form-label" htmlFor="email">Email</label>
              <div className="vds-input-wrapper">
                <Mail className="vds-input-icon h-5 w-5" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="vds-input"
                  required
                  data-testid="signup-email-input"
                />
              </div>
            </div>

            <div className="vds-form-group">
              <label className="vds-form-label" htmlFor="password">Password</label>
              <div className="vds-input-wrapper">
                <Lock className="vds-input-icon h-5 w-5" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="vds-input"
                  required
                  data-testid="signup-password-input"
                />
              </div>
            </div>

            <div className="vds-form-group">
              <label className="vds-form-label" htmlFor="confirmPassword">Confirm Password</label>
              <div className="vds-input-wrapper">
                <Lock className="vds-input-icon h-5 w-5" />
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="vds-input"
                  required
                  data-testid="signup-confirm-password-input"
                />
              </div>
            </div>

            {/* Terms Checkbox */}
            <div className="vds-checkbox-group">
              <input
                type="checkbox"
                id="terms"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="vds-checkbox"
                data-testid="signup-terms-checkbox"
              />
              <label htmlFor="terms" className="vds-checkbox-label">
                I have read and agree to the{' '}
                <Link to="/terms" target="_blank">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" target="_blank">Privacy Policy</Link>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || !accountType || !termsAccepted}
              className="vds-btn-auth-primary"
              data-testid="signup-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'CREATE ACCOUNT'
              )}
            </button>
          </form>
        </div>

        <div className="vds-auth-footer">
          <p className="vds-auth-footer-text">
            Already have an account?{' '}
            <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
